<template>
  <div>
    <!-- Search Container Card -->
    <n-search-container
      v-if="searchFields.length"
      :active="true"
      :show-header="false"
      show-search
      :loading="loading"
      @search="$emit('search', $event)"
      @reset="$emit('reset')"
    >
      <n-search-input
        ref="search"
        v-model="innerParams"
        :fields="searchFields"
      >
        <template #typeId="item">
          <b-form-group
            :label-for="item.field.key"
            :label="$t(item.field.label)"
          >
            <b-form-radio-group
              v-if="item.field.type === 'radio'"
              :id="item.field.key"
              :ref="item.field.key"
              v-model="params[item.field.key]"
              buttons
              button-variant="outline-primary rounded-0"
              class="flex-wrap"
            >
              <b-form-radio
                v-for="option in item.field.options"
                :key="option.value"
                :value="option.value"
              >
                {{ $t(option.text) }}
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </template>

        <template #period="item">
          <b-form-group
            :label-for="item.field.key"
            :label="$t(item.field.label)"
          >
            <b-form-radio-group
              v-if="item.field.type === 'radio'"
              :id="item.field.key"
              :ref="item.field.key"
              v-model="params[item.field.key]"
              buttons
              button-variant="outline-primary rounded-0"
              class="flex-wrap"
            >
              <b-form-radio
                v-for="option in item.field.options"
                :key="option.value"
                :value="option.value"
              >
                {{ $t(option.text) }}
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </template>

        <template
          v-for="slotName in searchSlotNames"
          #[slotName]="scope"
        >
          <slot
            :name="slotName"
            v-bind="scope"
          />
        </template>
      </n-search-input>
    </n-search-container>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-0-5">
        <b-row
          v-if="showBackButton || showRefreshButton"
          class="mb-2"
        >
          <b-col
            v-if="showBackButton"
            cols="6"
          >
            <b-button
              type="button"
              variant="outline-secondary"
              :disabled="loading"
              @click="$emit('back')"
            >
              <feather-icon icon="ChevronLeftIcon" />
              {{ $t('button.back') }}
            </b-button>
          </b-col>
          <b-col
            v-if="showRefreshButton"
            :cols="showBackButton ? 6 : 12"
            class="text-right"
          >
            <b-button
              type="button"
              variant="outline-secondary"
              :disabled="loading"
              @click="$emit('refresh')"
            >
              <feather-icon icon="RefreshCcwIcon" />
              {{ $t('button.refresh') }}
            </b-button>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{ $t('general.entries') }}</label>
            <v-select
              :value="perPage"
              :dir="isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
              @input="$emit('update:perPage', $event)"
            />
          </b-col>
        </b-row>
      </div>

      <div>
        <n-table
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="perPage"
          :total="total"
          @sort-changed="(...args) => $emit('sort-changed', ...args)"
        >
          <template #cell(username)="{ item }">
            <b-link
              v-if="typeId !== 5 && user.typeId !== 5"
              class="link-underline"
              @click="viewMember(item.userId)"
            >
              {{ item.username }}
            </b-link>
            <template v-else>{{ item.username }}</template>
            <small
              v-if="item.secondUsername"
              class="d-block text-wala"
            >
              ({{ item.secondUsername }})
            </small>
          </template>

          <template #cell(betAmount)="{ item }">
            <span
              :class="{
                'text-meron': item.betAmount < 0,
                'text-wala': item.betAmount >= 0,
              }"
            >
              {{ item.betAmount | currency(item.ccy) }}
            </span>
          </template>

          <template #cell(newWl)="{ item }">
            <b-link
              class="link-underline"
              :class="{
                'text-meron': item.newWl < 0,
                'text-wala': item.newWl >= 0,
              }"
              @click="showBetParleyHistory(item, true)"
            >
              {{ item.newWl | currency(item.ccy) }}
            </b-link>
          </template>

          <template #cell(outstanding)="{ item }">
            <b-link
              class="link-underline text-wala"
              @click="showBetParleyHistory(item, false)"
            >
              {{ item.outstanding | currency(item.ccy) }}
            </b-link>
          </template>

          <template #cell(wager)="{ item }">
            <b-link
              class="link-underline"
              @click="showBetParleyHistory(item, false)"
            >
              {{ item.wager }}
            </b-link>
          </template>

          <template #cell(member)="{ item }">
            <b-link
              class="link-underline"
              @click="viewMember(item.userId)"
            >
              {{ item.member }}
            </b-link>
          </template>

          <template
            v-if="headerLabel"
            #thead-top=""
          >
            <b-tr class="text-center">
              <b-th :colspan="typeId === 5 ? 10 : 14">
                {{ headerLabel }}
              </b-th>
            </b-tr>
          </template>

          <!-- Forward any extra cell(...) slots supplied by the parent -->
          <template
            v-for="slotName in tableSlotNames"
            #[slotName]="scope"
          >
            <slot
              :name="slotName"
              v-bind="scope"
            />
          </template>

          <template #custom-foot>
            <b-tr v-if="!loading">
              <b-th :colspan="user.typeId == 5 ? 3 : 4" />
              <b-th class="text-right">
                <h5
                  class="font-weight-bold"
                  :class="{
                    'text-wala': totalReport.betAmount >= 0,
                    'text-meron': totalReport.betAmount < 0,
                  }"
                >
                  {{ totalReport.betAmount | currency(totalReport.ccy) }}
                </h5>
              </b-th>
              <b-th>
                <h5 class="font-weight-bold">
                  {{ totalReport.wager }}
                </h5>
              </b-th>
              <b-th class="text-right">
                <h5
                  class="font-weight-bold"
                  :class="{
                    'text-meron': totalReport.newWl < 0,
                    'text-wala': totalReport.newWl >= 0,
                  }"
                >
                  {{ totalReport.newWl | currency(totalReport.ccy) }}
                </h5>
              </b-th>
              <b-th class="text-right">
                <h5 class="text-wala font-weight-bold">
                  {{ totalReport.outstanding | currency(totalReport.ccy) }}
                </h5>
              </b-th>
            </b-tr>
          </template>
        </n-table>

        <n-pagination
          ref="pagination"
          class="mt-2"
          :total="total"
          :per-page="perPage"
          :page="params.page"
          @change="$emit('page-change', $event)"
        />
      </div>
    </b-card>

    <bet-parley-history ref="betParleyHistory" />
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormRadio,
  BFormRadioGroup,
  BLink,
  BRow,
  BTh,
  BTr,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import NPagination from '@/components/NPagination.vue'
import NSearchContainer from '@/components/NSearchContainer.vue'
import NSearchInput from '@/components/NSearchInput.vue'
import NTable from '@/components/NTable.vue'
import BetParleyHistory from '@/views/report/parleyReport/betParleyHistory/Index.vue'

export default {
  name: 'ParleyReport',
  components: {
    BetParleyHistory,
    BLink,
    BFormRadioGroup,
    BFormRadio,
    BFormGroup,
    BCard,
    BRow,
    BCol,
    BButton,
    BTr,
    BTh,
    NPagination,
    NSearchContainer,
    NSearchInput,
    NTable,
    vSelect,
  },
  props: {
    fields: {
      type: Array,
      required: true,
    },
    searchFields: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    totalReport: {
      type: Object,
      default: () => {},
    },
    params: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: () => {},
    },
    loading: Boolean,
    total: {
      type: Number,
      default: 0,
    },
    perPage: {
      type: [Number, String],
      required: true,
    },
    perPageOptions: {
      type: Array,
      default: () => [10, 25, 50, 100],
    },
    isRTL: Boolean,
    headerLabel: {
      type: String,
      default: '',
    },
    showBackButton: Boolean,
    showRefreshButton: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    // v-model passthrough into <n-search-input>, keeps parent's `params`
    // object reactive without this component needing to know its shape.
    innerParams: {
      get() {
        return this.params
      },
      set(value) {
        this.$emit('update:params', value)
      },
    },
    // Any scoped slot the parent passes named "typeId", "period", etc.
    // (i.e. matching a search field key) goes to n-search-input.
    searchSlotNames() {
      const searchKeys = this.searchFields.map(f => f.key)
      return Object.keys(this.$scopedSlots)
        .filter(name => searchKeys.includes(name))
    },
    // Everything else (extra cell(xxx) overrides, etc.) forwarded to n-table.
    tableSlotNames() {
      const searchKeys = this.searchFields.map(f => f.key)
      return Object.keys(this.$scopedSlots)
        .filter(name => !searchKeys.includes(name))
    },
    userData() {
      return this.$store.state.profile.name
        ? this.$store.state.profile
        : JSON.parse(localStorage.getItem('userData'))
    },
    typeId() {
      if (this.$store.state.profile.typeId) {
        return this.$store.state.profile.typeId
      }
      return this.userData?.id
    },
  },
  methods: {
    viewMember(userId) {
      this.$emit('viewMember', userId)
    },
    showBetParleyHistory(item, parleyResult = true) {
      this.$refs.betParleyHistory.show(item, parleyResult, this.params)
    },
  },
}
</script>
